import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Maximize2, Smartphone } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PortalQRProjection() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public/portal/${slug}`);
      setData(r.data);
    } catch (e) {
      setData({ error: true });
    }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  if (!data) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center overline">Carregando…</div>;
  }
  if (data.error) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center overline">Portal não encontrado</div>;
  }

  const brand = data.brand || {};
  const primary = brand.brand_primary || "#C07C88";
  const gold = brand.brand_accent || "#D4AF37";
  const companyName = brand.company_name || "Leadera Corp";
  const portalUrl = `${window.location.origin}/portal/${slug}`;
  const qrSrc = `${BACKEND}/api/public/portal-qr/${slug}?origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="portal-qr-projection">
      <button
        onClick={goFullscreen}
        className="absolute top-6 right-6 w-11 h-11 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors z-20"
        title="Entrar em tela cheia (ou pressione F11)"
        data-testid="portal-qr-fullscreen"
      >
        <Maximize2 size={16} />
      </button>

      <div className="flex-1 flex flex-col items-center justify-center px-8 py-10">
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase tracking-[0.35em] font-medium" style={{ color: gold }}>{companyName}</p>
          <h1 className="font-heading text-5xl md:text-6xl mt-5 leading-tight max-w-4xl">
            {data.training.title}
          </h1>
          {data.training.instructor && (
            <p className="text-base md:text-lg text-white/60 mt-3">com {data.training.instructor}</p>
          )}
        </div>

        <div className="relative">
          <div
            className="absolute -inset-6 rounded-[32px] blur-2xl opacity-30"
            style={{ background: `radial-gradient(circle, ${primary} 0%, transparent 70%)` }}
          />
          <div className="relative bg-white p-6 md:p-8 rounded-2xl shadow-2xl">
            <img
              src={qrSrc}
              alt="QR Code do Portal"
              className="w-[380px] h-[380px] md:w-[480px] md:h-[480px] block"
              data-testid="portal-qr-image"
            />
          </div>
          <div
            className="absolute -top-3 -right-3 w-12 h-12 rounded-full border-4 border-[#0A0A0A] flex items-center justify-center"
            style={{ background: gold }}
          >
            <Smartphone size={20} className="text-[#0A0A0A]" strokeWidth={2.2} />
          </div>
        </div>

        <div className="text-center mt-10 max-w-2xl">
          <p className="font-heading text-3xl md:text-4xl" style={{ color: primary }}>Escaneie com a câmera</p>
          <p className="text-base md:text-lg text-white/55 mt-3">
            Aponte o celular para o QR Code acima para acessar materiais, autorizar imagem/voz e confirmar presença.
          </p>
          <p className="text-xs md:text-sm text-white/35 mt-6 font-mono break-all">{portalUrl}</p>
        </div>
      </div>

      <div className="border-t border-white/5 py-5 text-center">
        <p className="text-[10px] uppercase tracking-[0.35em] text-white/35">
          Portal do Aluno · {data.enrolled_count || 0} inscritos
          {data.vacancies_left !== null && data.vacancies_left !== undefined && (
            <span> · {data.vacancies_left} vagas</span>
          )}
        </p>
      </div>
    </div>
  );
}
